/**
 * Lightweight inline coachmark — a yellow callout box that points to the
 * element rendered immediately above it. No portal, no positioning math:
 * caller renders <Coachmark /> right after the target card.
 */
import { useTranslation } from 'react-i18next';
import { markCoachmarkSeen } from '@/features/promo/tutorial-state.js';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function Coachmark({ open, onClose }: Props) {
  const { t } = useTranslation('promo');
  if (!open) return null;

  const dismiss = () => {
    markCoachmarkSeen();
    onClose();
  };

  return (
    <div className="relative rounded-xl border border-[#fbbf24] bg-[var(--bg-elevated)] p-3 mt-2">
      <div
        className="absolute -top-1.5 left-3.5 w-2.5 h-2.5 bg-[var(--bg-elevated)] border-t border-l border-[#fbbf24]"
        style={{ transform: 'rotate(45deg)' }}
      />
      <div className="flex items-start gap-2">
        <div className="w-[22px] h-[22px] rounded-full bg-[#fbbf24] text-black font-bold flex items-center justify-center text-[11px]">!</div>
        <div className="flex-1">
          <div className="text-[11px] font-bold text-[#fbbf24]">{t('coachmark.title')}</div>
          <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">
            {t('coachmark.body')}
          </div>
          <div className="flex justify-end gap-2 mt-1.5">
            <button onClick={dismiss} className="text-[10px] text-[var(--text-muted)]">
              {t('coachmark.dismiss')}
            </button>
            <button
              onClick={dismiss}
              className="text-[10px] font-bold text-black bg-[#fbbf24] px-2 py-0.5 rounded-md"
            >
              {t('coachmark.gotIt')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
